// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import type { CanonicalUnit, ShoppingListPatchRequest } from "@bitebalance/contracts"

type ManualShoppingItemInput = NonNullable<ShoppingListPatchRequest["addManualItems"]>[number]
import { useI18n } from "@/i18n"
import { unitLabel } from "@/lib/units"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

export interface ManualAddFormProps {
  onAdd: (item: ManualShoppingItemInput) => void
  adding: boolean
}

/** Manual shopping-list item add (T13): name + optional quantity, defaulting to "1 count". */
export function ManualAddForm({ onAdd, adding }: ManualAddFormProps) {
  const { t } = useI18n()
  const [name, setName] = useState("")
  const [amount, setAmount] = useState(1)
  const [unit, setUnit] = useState<CanonicalUnit>("count")

  function handleSubmit() {
    if (!name.trim()) return
    onAdd({
      displayName: name.trim(),
      quantity: {
        canonical: { amount, unit },
        display: { quantity: amount, unit, unitKey: `unit.${unit}` },
      },
    })
    setName("")
    setAmount(1)
    setUnit("count")
  }

  return (
    <div className="flex flex-col gap-2 rounded-bb-lg border border-dashed border-line p-3">
      <p className="text-sm font-medium text-ink">{t("views.shop.addManualTitle")}</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-ink-2">
          {t("views.shop.addManualName")}
          <Input value={name} onChange={e => setName(e.target.value)} className="w-40" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-2">
          {t("views.shop.addManualQty")}
          <div className="flex gap-1">
            <Input
              type="number"
              min={0}
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              className="w-20"
            />
            <select
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
              value={unit}
              onChange={e => setUnit(e.target.value as CanonicalUnit)}
            >
              <option value="count">{unitLabel("count", t)}</option>
              <option value="g">g</option>
              <option value="ml">ml</option>
            </select>
          </div>
        </label>
        <Button
          type="button"
          variant="outline"
          className="min-h-9"
          disabled={!name.trim() || adding}
          onClick={handleSubmit}
        >
          {t("views.shop.addManualButton")}
        </Button>
      </div>
    </div>
  )
}
